import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemeId = 'dark' | 'ivory';

type ThemeCtx = { theme: ThemeId; setTheme: (t: ThemeId) => void; toggleTheme: () => void };

const Ctx = createContext<ThemeCtx | null>(null);
const STORAGE_KEY = 'termix_theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'ivory' || v === 'dark') return v;
    } catch { /* ignore */ }
    return 'dark';
  });

  const setTheme = useCallback((t: ThemeId) => {
    // Sync DOM before React re-renders so ChartPane reads the new CSS vars immediately.
    try { document.documentElement.setAttribute('data-theme', t); } catch { /* ignore */ }
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme: () => setTheme(theme === 'dark' ? 'ivory' : 'dark'),
  }), [theme, setTheme]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useTheme = (): ThemeCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme outside ThemeProvider');
  return v;
};
